import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '@env/environment';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private enabled = false;
  private initialized = false;
  private activePagePath: string | null = null;
  private activePageStartedAt: number | null = null;
  private readonly minimumEngagementMs = 1000;
  private readonly sensitiveRoutes = ['/auth/recover', '/device-access-request'];
  private readonly sensitiveQueryParams = ['token', 'tk', 'state', 'return_to'];

  constructor(private router: Router) {}

  init(): void {
    const gaId = (environment as any).googleAnalyticsId;
    if (!gaId || this.initialized || typeof window === 'undefined' || typeof document === 'undefined') return;

    this.initialized = true;
    this.bootstrap(gaId);
    this.enabled = true;
    this.bindPageLifecycleEvents();

    if (this.router.navigated) {
      this.startTrackingPage(this.router.url, true);
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.startTrackingPage(event.urlAfterRedirects, true);
      });
  }

  trackEvent(action: string, category: string, label?: string, value?: number): void {
    if (!this.enabled) return;

    this.emit('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }

  trackPatientSectionView(patientSection: string): void {
    if (!this.enabled) return;

    this.emit('event', 'patient_section_viewed', {
      patient_section: patientSection,
      event_category: 'patient_page',
    });
  }

  private bootstrap(gaId: string): void {
    window.dataLayer = window.dataLayer || [];

    if (typeof window.gtag !== 'function') {
      window.gtag = (...args: unknown[]) => {
        window.dataLayer.push(args);
      };
    }

    if (!document.querySelector(`script[data-google-analytics-id="${gaId}"]`)) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      script.setAttribute('data-google-analytics-id', gaId);
      document.head.appendChild(script);
    }

    this.emit('js', new Date());
    this.emit('config', gaId, { send_page_view: false });
  }

  private startTrackingPage(pagePath: string, emitPageView: boolean): void {
    const url = this.parseUrl(pagePath);
    const nextPagePath = url && !this.shouldSkipPageView(url) ? this.sanitizePath(url.pathname) : null;

    this.flushPageEngagement('navigation');
    this.activePagePath = nextPagePath;
    this.activePageStartedAt = nextPagePath && document.visibilityState === 'visible' ? Date.now() : null;

    if (!nextPagePath || !emitPageView) return;

    this.emit('event', 'page_view', {
      page_path: nextPagePath,
    });
  }

  private emit(...args: unknown[]): void {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    window.gtag(...args);
  }

  private parseUrl(url: string): URL | null {
    if (typeof window === 'undefined') return null;

    try {
      return new URL(url, window.location.origin);
    } catch {
      return null;
    }
  }

  private shouldSkipPageView(url: URL): boolean {
    const pathname = url.pathname;
    const hasSensitiveRoute = this.sensitiveRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`));

    if (hasSensitiveRoute) return true;

    return this.sensitiveQueryParams.some((param) => url.searchParams.has(param));
  }

  private sanitizePath(pathname: string): string {
    const segments = pathname.split('/').map((segment) => {
      if (!segment) return segment;

      return this.isSensitiveIdentifier(segment) ? ':id' : segment;
    });

    return segments.join('/') || '/';
  }

  private isSensitiveIdentifier(segment: string): boolean {
    return /^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment);
  }

  private bindPageLifecycleEvents(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushPageEngagement('hidden');
        return;
      }

      if (this.activePagePath && this.activePageStartedAt === null) {
        this.activePageStartedAt = Date.now();
      }
    });

    window.addEventListener('pagehide', () => {
      this.flushPageEngagement('pagehide');
    });
  }

  private flushPageEngagement(trigger: 'navigation' | 'hidden' | 'pagehide'): void {
    if (!this.activePagePath || this.activePageStartedAt === null) return;

    const engagementTimeMs = Date.now() - this.activePageStartedAt;
    this.activePageStartedAt = null;

    if (engagementTimeMs < this.minimumEngagementMs) return;

    this.emit('event', 'page_engagement', {
      page_path: this.activePagePath,
      engagement_time_msec: engagementTimeMs,
      engagement_time_sec: Math.round(engagementTimeMs / 1000),
      engagement_trigger: trigger,
      transport_type: 'beacon',
    });
  }
}
